import React, { useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { FormInstance } from "antd/es/form/hooks/useForm";
import Select from "antd/es/select";
import cn from "classnames";
import isEqual from "lodash-es/isEqual";
import { defineMessages, useIntl } from "react-intl";

import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { FormLayoutRoleAssociationRecord } from "@mapmycustomers/shared/types/layout/FormLayoutRoleAssociation";
import Role from "@mapmycustomers/shared/types/Role";
import Team from "@mapmycustomers/shared/types/Team";
import { Collapse, CollapsePanel, SearchField, SelectField } from "@mapmycustomers/ui";

import defaultFilterOption from "@app/component/input/utils/defaultFilterOption";
import { getRolesExcludingOwners, getTeams } from "@app/store/members";
import { RootState } from "@app/store/rootReducer";
import { getRoleDisplayNameAlt } from "@app/util/ui";

import { getLayouts, isLoading } from "../../store";
import DefaultAssociationFormValues from "../../type/DefaultAssociationFormValues";
import { NO_TEAM_ID } from "../../util/const";
import mapTeamRecord from "../../util/mapTeamRecord";

import styles from "./TeamLayoutsGrid.module.scss";

interface Props {
  associations: Array<FormLayoutRoleAssociationRecord>;
  className?: string;
  form: FormInstance<DefaultAssociationFormValues>;
  layouts: FormLayout[];
  loading: boolean;
  onChange?: (hasChanges: boolean) => void;
  roles: Role[];
  teams: Team[];
}

const messages = defineMessages({
  noTeam: {
    id: "settings.formLayouts.teamRoles.noTeam",
    defaultMessage: "Users without a team",
    description: "Users without a team label",
  },
  placeholder: {
    id: "settings.formLayouts.teamRoles.search.placeholder",
    defaultMessage: "Search Teams",
    description: "Search Teams placeholder",
  },
});

const TeamLayoutsGrid: React.FC<Props> = ({
  associations,
  className,
  form,
  layouts,
  loading,
  onChange,
  roles,
  teams,
}) => {
  const intl = useIntl();

  const [value, setValue] = useState<Array<FormLayoutRoleAssociationRecord>>(associations);
  const [search, setSearch] = useState<string>("");

  const roleMap = useMemo(() => {
    const map = new Map<Team["id"], Map<Role["id"], FormLayout["id"]>>();

    value.forEach((entry: FormLayoutRoleAssociationRecord) => {
      const teamId = entry.team?.id ?? NO_TEAM_ID;

      const teamRolesMap = map.get(teamId) ?? new Map();
      teamRolesMap.set(entry.role.id, entry.layout.id);
      map.set(teamId, teamRolesMap);
    });

    return map;
  }, [value]);

  const filteredTeams = useMemo(() => {
    let result: Array<Pick<Team, "id" | "name">> = [
      {
        id: NO_TEAM_ID,
        name: intl.formatMessage(messages.noTeam),
      },
      ...teams.map(mapTeamRecord),
    ];

    if (search) {
      const searchPhrase = search.trim().toLowerCase();
      if (searchPhrase) {
        result = result.filter((team) => team.name.toLowerCase().indexOf(searchPhrase) >= 0);
      }
    }

    return result.sort((a, b) =>
      a.id === NO_TEAM_ID ? -1 : b.id === NO_TEAM_ID ? 1 : a.name.localeCompare(b.name)
    );
  }, [intl, search, teams]);

  const handleRoleUpdate = useCallback(
    (teamId: Team["id"], roleId: Role["id"], layoutId: FormLayout["id"]) => {
      const updatedValue: Array<FormLayoutRoleAssociationRecord> = [];

      let found = false;
      roleMap.forEach((teamRoleMap, roleTeamId: FormLayout["id"]) => {
        teamRoleMap.forEach((roleLayoutId, id: Role["id"]) => {
          const newRecord: FormLayoutRoleAssociationRecord = {
            layout: {
              id: roleTeamId === teamId && id === roleId ? layoutId : roleLayoutId,
            },
            role: {
              id,
            },
            team:
              roleTeamId === NO_TEAM_ID || roleTeamId === null
                ? null
                : {
                    id: roleTeamId,
                  },
          };
          updatedValue.push(newRecord);
        });

        if (teamRoleMap.has(roleId) && roleTeamId === teamId) {
          found = true;
        }
      });

      if (!found) {
        const newRecord: FormLayoutRoleAssociationRecord = {
          layout: {
            id: layoutId,
          },
          role: {
            id: roleId,
          },
          team:
            teamId === NO_TEAM_ID
              ? null
              : {
                  id: teamId,
                },
        };
        updatedValue.push(newRecord);
      }

      onChange?.(!isEqual(associations, updatedValue));

      form.setFieldsValue({
        value: updatedValue,
      });

      setValue(updatedValue);
    },
    [associations, form, onChange, roleMap]
  );

  const handleSearch = useCallback((value) => setSearch(value), [setSearch]);

  return (
    <div className={cn(styles.container, className)}>
      <div className={styles.searchBox}>
        <SearchField
          allowClear
          onChange={handleSearch}
          placeholder={intl.formatMessage(messages.placeholder)}
          size="large"
        />
      </div>

      <div className={styles.list}>
        <Collapse className={styles.collapse}>
          {filteredTeams.map((team) => (
            <CollapsePanel header={team.name} key={team.id ?? NO_TEAM_ID}>
              <div className={styles.teamRoles}>
                {roles.map((role) => {
                  const teamId = team.id ?? NO_TEAM_ID;
                  const layoutId = roleMap.get(teamId)?.get(role.id) ?? "";

                  return (
                    <div className={styles.role} key={role.id}>
                      <SelectField<"" | FormLayout["id"]>
                        className={styles.select}
                        dropdownMatchSelectWidth={false}
                        filterOption={defaultFilterOption}
                        label={role.name ?? getRoleDisplayNameAlt(intl, role.key)}
                        loading={loading}
                        onChange={(value) => {
                          if (value) {
                            handleRoleUpdate(teamId, role.id, value);
                          }
                        }}
                        showSearch
                        value={layoutId}
                      >
                        {layouts.map((layout) => (
                          <Select.Option key={layout.id} label={layout.name} value={layout.id}>
                            {layout.name}
                          </Select.Option>
                        ))}
                      </SelectField>
                    </div>
                  );
                })}
              </div>
            </CollapsePanel>
          ))}
        </Collapse>
      </div>
    </div>
  );
};

const mapStateToProps = (state: RootState) => ({
  layouts: getLayouts(state),
  loading: isLoading(state),
  roles: getRolesExcludingOwners(state),
  teams: getTeams(state),
});

export default connect(mapStateToProps)(TeamLayoutsGrid);
